import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';

import { Form } from 'antd';

import { DASHBOARD_ITEM_API_NAMES, SCHEDULE_TYPES } from '@src/constants/commons';
import useSelectGroupsWithPagination from '@src/packages/utils/hooks/collections/useSelectGroupsWithPagination';
import BarChartService from '@src/services/BarChartService';

import {
  ALL_TEMPLATES,
  byPassTaskByTemplate,
  DASHBOARDS_EVENT_TYPE,
  DIVISIONS_TYPE,
  EXPRESSION_VALUE_TYPE_DEFAULT,
  GROUP_BY_DEFAULT,
  GROUP_BY_OPTIONS_VALUES,
  INITIAL_DISABLE_SCHEDULE_TYPE,
  LAYOUT_BY_COMPONENT_TYPE,
  TEMPLATE_IDS_DEFAULT,
  USERS_TYPE,
} from '../../../constants';
import { useManageDivisionValue } from '../../../hooks';

const PAGE_SIZE = 20;

const getDisabledTypesByGroupBy = groupBy => {
  switch (groupBy) {
    case GROUP_BY_OPTIONS_VALUES.SCHEDULES:
      return {
        ...INITIAL_DISABLE_SCHEDULE_TYPE,
        tasks: true,
        null: true,
        checkpoint_route: true,
      };
    case GROUP_BY_OPTIONS_VALUES.TASKS:
      return {
        ...INITIAL_DISABLE_SCHEDULE_TYPE,
        schedules: true,
        checkpoint_route: true,
        null: true,
      };
    case GROUP_BY_OPTIONS_VALUES.ROUTES:
      return {
        ...INITIAL_DISABLE_SCHEDULE_TYPE,
        schedules: true,
        tasks: true,
        null: true,
      };
    default:
      return INITIAL_DISABLE_SCHEDULE_TYPE;
  }
};

const useScheduleComplianceForm = ({ chartId, divId, boardId }) => {
  const [loading, setLoading] = useState(false);
  const [selectedSchedules, setSelectedSchedules] = useState({ byId: {}, selectedAll: false });
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [disableType, setDisableType] = useState(INITIAL_DISABLE_SCHEDULE_TYPE);
  const [fetchedChartData, setFetchedChartData] = useState(null);
  const [initialScheduleIds, setInitialScheduleIds] = useState([]);

  const [form] = Form.useForm();
  const barChartService = useMemo(() => new BarChartService(), []);

  const orgId = useSelector(state => state.organizations.organization.id);
  const watchDivisionFilter = Form.useWatch('divisionFilter', form);
  const divisionIds = Form.useWatch('divisionIds', form);
  const watchUsersType = Form.useWatch('usersType', form);
  const watchType = Form.useWatch('type', form);
  const divisionIdsToSubmit = useManageDivisionValue({
    divisionFilterValue: watchDivisionFilter,
    divisionIds,
    divId,
  });

  const userOrgDivisions = useSelector(({ divisions }) => divisions?.assigned?.data?.divisions);

  const divisionsOptions = useMemo(
    () =>
      Object.values(userOrgDivisions || {})
        .map(division => ({
          label: division.name,
          value: division.id,
        }))
        .filter(division => (divId ? division.value === divId : true)),
    [userOrgDivisions, divId],
  );

  const {
    data: groupOptions,
    loading: groupsLoading,
    handlePopupScroll: handlePopupScrollGroups,
  } = useSelectGroupsWithPagination({
    orgId,
    pageSize: PAGE_SIZE,
    divIds: divId ? [divId] : null,
    orderByConfig: { field: 'name', direction: 'asc' },
  });

  const handleClear = useCallback(() => {
    form.resetFields();
    setSelectedSchedules({ byId: {}, selectedAll: false });
  }, [form]);

  const buildBody = useCallback(() => {
    const title = form.getFieldValue('title');
    const description = form.getFieldValue('description');
    const userIds = form.getFieldValue('userIds');
    const groupIds = form.getFieldValue('groupIds');
    const schedulesIds = form.getFieldValue('schedulesIds');
    const groupBy = form.getFieldValue('groupBy');
    const valueType = form.getFieldValue('valueType');
    const type = form.getFieldValue('type');
    const layout = form.getFieldValue('layout');
    const templateIds = form.getFieldValue('templateIds');
    const currentUsersType = watchUsersType || USERS_TYPE.ALL;

    const usersOrGroups = {};
    if (currentUsersType === USERS_TYPE.ALL) {
      usersOrGroups.userIds = [];
    } else if (currentUsersType === USERS_TYPE.USERS) {
      usersOrGroups.userIds = userIds;
      usersOrGroups.groupIds = [];
    } else if (currentUsersType === USERS_TYPE.GROUPS) {
      usersOrGroups.groupIds = groupIds;
      usersOrGroups.userIds = [];
    }

    const body = {
      title,
      description,
      layout: layout || LAYOUT_BY_COMPONENT_TYPE[DASHBOARDS_EVENT_TYPE.BAR_CHARTS],
      apiName: DASHBOARD_ITEM_API_NAMES.SCHEDULES_COMPLIANCE,
      queryParams: {
        ...usersOrGroups,
        scheduleIds: selectedSchedules.selectedAll ? [] : schedulesIds,
        divisionIds: divisionIdsToSubmit,
        templateIds: templateIds?.includes(ALL_TEMPLATES) ? [] : templateIds,
        groupBy: byPassTaskByTemplate(groupBy),
        valueType,
        type,
      },
    };

    form.setFieldValue('formData', body);
    return body;
  }, [divisionIdsToSubmit, form, selectedSchedules.selectedAll, watchUsersType]);

  const resetSelectedSchedules = useCallback(() => {
    setSelectedSchedules({ byId: {}, selectedAll: true });
    form.setFieldValue('schedulesIds', []);
  }, [form]);

  const resetTemplateIds = useCallback(() => {
    form.setFieldValue('templateIds', []);
  }, [form]);

  const resetUsersAndGroups = useCallback(() => {
    form.setFieldValue('userIds', []);
    form.setFieldValue('groupIds', []);
  }, [form]);

  const onChangeGroupBy = useCallback(
    ({ target }) => {
      const value = target.value;
      setDisableType(getDisabledTypesByGroupBy(value));

      switch (value) {
        case GROUP_BY_OPTIONS_VALUES.SCHEDULES:
          form.setFieldValue('type', SCHEDULE_TYPES.schedule);
          break;
        case GROUP_BY_OPTIONS_VALUES.TASKS:
          form.setFieldValue('type', SCHEDULE_TYPES.task);
          break;
        case GROUP_BY_OPTIONS_VALUES.ROUTES:
          form.setFieldValue('type', SCHEDULE_TYPES.checkpoint_route);
          break;
        default:
          form.setFieldValue('type', null);
          break;
      }

      form.setFieldValue('schedulesIds', []);
      form.setFieldValue('templateIds', []);
    },
    [form],
  );

  useEffect(() => {
    setSelectedSchedules({ byId: {}, selectedAll: true });
    form.setFieldValue('schedulesIds', []);
  }, [form, watchType]);

  useEffect(() => {
    form.setFieldValue('schedulesIds', Object.keys(selectedSchedules?.byId || {}));
  }, [selectedSchedules, form]);

  useEffect(() => {
    if (!chartId || !boardId || !orgId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await barChartService.getBarChart({ orgId, divId, boardId, chartId });
        if (res) {
          setFetchedChartData(res);
          const {
            title: titleFetched,
            description: descriptionFetched,
            queryParams: queryParamsFetched = {},
            layout: layoutFetched,
          } = res;

          const {
            userIds: userIdsFetched = [],
            groupIds: groupIdsFetched = [],
            scheduleIds: scheduleIdsFetched = [],
            divisionIds: divisionIdsFetched = [],
            groupBy: groupByFetched,
            valueType: valueTypeFetched,
            type: typeFetched,
            templateIds: templateIdsFetched = [],
          } = queryParamsFetched;

          let usersTypeFetched = USERS_TYPE.ALL;
          if (userIdsFetched?.length > 0) {
            usersTypeFetched = USERS_TYPE.USERS;
          } else if (groupIdsFetched?.length > 0) {
            usersTypeFetched = USERS_TYPE.GROUPS;
          }

          let divisionFilterFetched = DIVISIONS_TYPE.ALL;
          if (divisionIdsFetched?.length > 0) {
            divisionFilterFetched = DIVISIONS_TYPE.DIVISIONS;
          }

          const groupByValue = byPassTaskByTemplate(groupByFetched) || GROUP_BY_DEFAULT;
          setDisableType(getDisabledTypesByGroupBy(groupByValue));

          form.setFieldsValue({
            title: titleFetched || '',
            description: descriptionFetched || '',
            userIds: userIdsFetched || [],
            groupIds: groupIdsFetched || [],
            schedulesIds: scheduleIdsFetched || [],
            divisionIds: divisionIdsFetched || [],
            groupBy: groupByValue,
            valueType: valueTypeFetched || EXPRESSION_VALUE_TYPE_DEFAULT,
            type: typeFetched ?? null,
            divisionFilter: divisionFilterFetched,
            usersType: usersTypeFetched,
            layout: layoutFetched || LAYOUT_BY_COMPONENT_TYPE[DASHBOARDS_EVENT_TYPE.BAR_CHARTS],
            templateIds: templateIdsFetched || TEMPLATE_IDS_DEFAULT,
            id: chartId,
          });
          setInitialScheduleIds(scheduleIdsFetched);
        }
      } catch (error) {
        console.error('Error loading schedule compliance chart:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [boardId, divId, orgId, chartId, form, barChartService]);

  useEffect(() => {
    if (fetchedChartData && !loadingSchedules && chartId) {
      const { scheduleIds: scheduleIdsFetched = [] } = fetchedChartData?.queryParams || {};

      if (scheduleIdsFetched?.length > 0) {
        const schedulesById = {};
        scheduleIdsFetched.forEach(id => {
          schedulesById[id] = true;
        });
        setSelectedSchedules({ byId: schedulesById, selectedAll: false });
        form.setFieldValue('schedulesIds', scheduleIdsFetched);
      } else {
        setSelectedSchedules({ byId: {}, selectedAll: true });
      }

      setFetchedChartData(null);
    }
  }, [loadingSchedules, fetchedChartData, chartId, form]);

  return {
    form,
    loading,
    selectedSchedules,
    setSelectedSchedules,
    setLoadingSchedules,
    disableType,
    fetchedChartData,
    initialScheduleIds,
    orgId,
    divisionIds,
    watchDivisionFilter,
    watchUsersType,
    watchType,
    divisionsOptions,
    groupOptions,
    groupsLoading,
    handlePopupScrollGroups,
    handleClear,
    buildBody,
    resetSelectedSchedules,
    resetTemplateIds,
    onChangeGroupBy,
    resetUsersAndGroups
  };
};

export default useScheduleComplianceForm;
